import { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Ambulance, MapPin, Phone, PlayCircle, CheckCircle2, PackageCheck, Navigation } from "lucide-react";

const NEXT = [
  { key: "accepted", label: "Accept ride", icon: PlayCircle, color: "bg-slate-900" },
  { key: "en_route_to_pickup", label: "En route to pickup", icon: Navigation, color: "bg-amber-600" },
  { key: "at_pickup", label: "At pickup", icon: MapPin, color: "bg-cyan-600" },
  { key: "en_route_to_hospital", label: "En route to hospital", icon: Navigation, color: "bg-blue-600" },
  { key: "arrived", label: "Arrived", icon: PackageCheck, color: "bg-emerald-600" },
];

export default function DriverApp() {
  const { user } = useAuth();
  const [rides, setRides] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get("/driver/rides");
      setRides(data);
    } catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, []);

  const setStatus = async (id, status) => {
    try {
      await api.post(`/driver/rides/${id}/status`, { status });
      toast.success(`Status → ${status.replace(/_/g, " ")}`);
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const openMap = (r) => {
    const stage = r.status;
    const dest = stage === "at_pickup" || stage === "en_route_to_hospital"
      ? [r.destination_lat, r.destination_lng]
      : [r.pickup_lat, r.pickup_lng];
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${dest[0]},${dest[1]}&travelmode=driving`, "_blank");
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 pb-24 sm:pb-8">
      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-red-700">Driver App</div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold mt-1">Hi {user?.name?.split(" ")[0]}</h1>
          <p className="text-slate-600 text-sm">Your assigned rides will appear here.</p>
        </div>
      </div>

      {rides.length === 0 && (
        <Card className="p-10 rounded-2xl text-center text-slate-500" data-testid="no-rides">
          <Ambulance className="w-8 h-8 mx-auto text-slate-300 mb-3" />
          No active rides. When a booking arrives, you'll see it here.
        </Card>
      )}

      {rides.map((r) => (
        <Card key={r.id} className="p-5 rounded-2xl mb-3" data-testid={`ride-${r.id}`}>
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-lg bg-red-500/15 text-red-700 flex items-center justify-center"><Ambulance className="w-5 h-5" /></div>
                <div>
                  <div className="font-display font-bold">{r.contact}</div>
                  <div className="text-xs text-slate-500 uppercase tracking-widest">{r.ambulance_type} · {r.vehicle_number}</div>
                </div>
              </div>
              <div className="mt-3 text-sm">
                <div><strong className="text-teal-700">Pickup:</strong> {r.pickup_address}</div>
                <div className="mt-1"><strong className="text-cyan-700">Drop:</strong> {r.destination_hospital_name || r.drop_address}</div>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                <Badge className="bg-red-100 text-red-800 border-0">{(r.status || "dispatched").replace(/_/g, " ")}</Badge>
                {r.distance_km != null && <span>{r.distance_km} km · ₹{r.amount}</span>}
              </div>
            </div>
            <a href={`tel:${r.contact}`} data-testid={`call-${r.id}`}>
              <Button variant="outline" size="sm"><Phone className="w-4 h-4 mr-1" /> Call patient</Button>
            </a>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => openMap(r)} data-testid={`nav-${r.id}`}>
              <Navigation className="w-4 h-4 mr-2" /> Open in Maps
            </Button>
            {NEXT.map((n) => (
              <Button key={n.key} size="sm" className={`${n.color} text-white`} onClick={() => setStatus(r.id, n.key)} data-testid={`ds-${n.key}-${r.id}`}>
                <n.icon className="w-4 h-4 mr-1" /> {n.label}
              </Button>
            ))}
          </div>
        </Card>
      ))}
    </div>
  );
}
